import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserProfile } from "../../context/UserProfileContext";
import { BuildingIcon, TrophyIcon } from "../../images/index";
import { getOrganisationAnalytics } from "../../services/analyticsService";
import { getOrganisationDetails } from "../../services/organizationService";
import { AnalyticsData, Organisation } from "../../types/types";

export const useHomeHook = () => {
  const navigate = useNavigate();
  const { userProfile } = useUserProfile();

  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const orgId = userProfile?.organisation_id
    ? String(userProfile.organisation_id)
    : undefined;

  // Fetch summary data on mount
  useEffect(() => {
    if (!orgId) {
      setSummaryLoading(false);
      return;
    }

    let cancelled = false;
    setSummaryLoading(true);

    Promise.allSettled([
      getOrganisationAnalytics(orgId, { range: "30d" }),
      getOrganisationDetails(orgId),
    ]).then(([analyticsResult, orgResult]) => {
      if (cancelled) return;
      if (analyticsResult.status === "fulfilled") {
        setAnalytics(analyticsResult.value);
      }
      if (orgResult.status === "fulfilled") {
        setOrganisation(orgResult.value);
      }
      setSummaryLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [orgId]);

  // Quick actions configuration
  const quickActions = [
    {
      id: "organizations",
      title: "User Management",
      description: "View and manage organization users details",
      icon: <BuildingIcon className="w-8 h-8 text-blue-600" />,
      path: `/organisation/${orgId}`,
      color: "border-blue-200 bg-blue-50",
    },
    {
      id: "analytics",
      title: "Analytics Dashboard",
      description: "View detailed analytics and reports",
      icon: <TrophyIcon className="w-8 h-8 text-green-600" />,
      path: `/analytics/dashboard/${orgId}`,
      color: "border-green-200 bg-green-50",
    },
  ];

  // Navigation handler
  const handleQuickActionClick = (path: string) => {
    navigate(path);
  };

  return {
    // Data
    userProfile,
    greeting: getGreeting(),
    quickActions,
    analytics,
    organisation,
    summaryLoading,
    organizationId: orgId,

    // Actions
    handleQuickActionClick,
  };
};
